import { TelnyxRTC } from '@telnyx/webrtc';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import { useCallSessionStore, SESSION_STATUS } from '../../stores/callSessionStore';

/**
 * La sesión de llamadas, fuera de React.
 *
 * Esto **no es un hook y no es un componente**, y ahí está todo el sentido: la
 * sesión de un gestor no puede depender de que una pantalla esté montada. Antes
 * vivía dentro de `CallSessionPage`, que la desconectaba al desmontarse, así que
 * navegar a cualquier otra pantalla cortaba el audio y dejaba al gestor fuera del
 * marcador sin haberlo pedido.
 *
 * La regla ahora es la que pidió Edgar: **si el gestor no ha pausado a mano, la
 * sesión sigue**. Solo la cierran tres cosas — el botón de cerrar, cerrar la
 * pestaña, o dejar de dar señales durante dos minutos.
 *
 * El cliente de Telnyx vive en una variable de módulo por el mismo motivo por el
 * que el store lo saca de React: es un objeto con una conexión viva, no estado
 * serializable. No se compara por igualdad ni se vuelve a crear en cada render.
 */

/** El único cliente WebRTC de la aplicación. */
let client = null;

/**
 * Avisos del SDK que significan **que no nos están oyendo**.
 *
 * Solo los de NUESTRA pata, a propósito. `LOW_INBOUND_AUDIO` queda fuera: que el
 * deudor esté callado no es un problema que el gestor pueda arreglar, y sacarlo
 * en pantalla sería el cuento del lobo hasta que nadie mire ninguno.
 *
 * El 23/09/2026 el SDK estuvo una llamada entera emitiendo `31005 LOW_LOCAL_AUDIO`
 * —«Low local microphone audio detected», con «micrófono equivocado» y «ganancia
 * del sistema muteada» entre sus causas— mientras la pantalla decía «En
 * conversación» tan tranquila. El deudor no oyó nada y nadie se enteró hasta
 * abrir la consola.
 */
const AVISOS_DE_MICROFONO = new Set(['LOW_LOCAL_AUDIO', 'LOW_BYTES_SENT']);

/**
 * El micrófono **en crudo**, sin la cadena de procesado de Chrome.
 *
 * Exportado para que el test afirme los tres valores sin copiarlos: una copia
 * en el test aprobaría igual si alguien cambia uno de los tres en el código.
 */
export const SIN_PROCESADO = {
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
};

/** Evita que dos pulsaciones seguidas abran dos sesiones. */
let busy = false;

/**
 * Pide el micrófono ANTES de abrir la sesión.
 *
 * 🔴 Esto no es una comodidad, es la diferencia entre oír y no oír. El marcador
 * **descuelga solo** (`call.answer()` al recibir `ringing`), así que si el permiso
 * no está concedido cuando entra la primera llamada, Chrome enseña su diálogo
 * **mientras la llamada ya se está estableciendo**: se descuelga sin pista de
 * audio local y el deudor **no oye al gestor durante toda la conversación**,
 * aunque después se conceda el permiso.
 *
 * Pasó en la primera llamada con audio, el 23/09/2026: 12 segundos entre el
 * descuelgue y el puente —que fueron exactamente el diálogo del permiso— y audio
 * de una sola dirección. El SDK no avisó de nada, porque para él no había fallo:
 * simplemente no había micrófono que mandar.
 *
 * Y de paso arregla un fallo de producto: la pantalla ponía **«Listo»** sin
 * comprobar que hubiera micrófono. El gestor se creía operativo mientras su
 * navegador rechazaba o enmudecía una llamada tras otra, y nada se lo decía.
 *
 * @returns {Promise<boolean>} si hay micrófono utilizable.
 */
async function asegurarMicrofono() {
  if (!navigator.mediaDevices?.getUserMedia) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // 🔴 **LA PISTA SE SUELTA, Y NO SE PUEDE DEJAR ABIERTA.**
    //
    // Dejarla abierta es lo primero que se le ocurre a cualquiera, porque el SDK
    // tarda ~3,9 s en abrir el dispositivo en cada llamada («Media devices
    // acquired 3857.70ms» en su propio desglose) y mantenerlo caliente lo baja a
    // ~50 ms. Se probó el 24/09/2026 y **rompe el audio de salida**: el SDK abre
    // su PROPIA captura del mismo dispositivo, Windows le entrega una pista
    // muda, y el deudor no oye al gestor en toda la conversación.
    //
    // El único síntoma era `warning 31005: Low local microphone audio detected`
    // en la consola, que es fácil de atribuir a que el gestor no estaba hablando.
    // Lo que lo demostró fue la GRABACIÓN: dos conversaciones reales de 18 y 16
    // segundos en las que **solo se oye al deudor**.
    //
    // Los ~3,9 s son caros —el deudor descuelga y oye silencio— pero son
    // recuperables: el arreglo bueno es pasarle ESTA pista al SDK
    // (`CallOptions.localStream`) para que haya UNA sola captura, no dos. Hasta
    // que eso esté probado con una llamada real, se suelta.
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch {
    return false;
  }
}

/**
 * Cada cuánto se le dice al backend que seguimos vivos.
 *
 * Tiene que ser **holgadamente menor** que el `STALE_SESSION_MS` del backend (2
 * min): si se acercara, un tick perdido por una pestaña en segundo plano bastaría
 * para que el marcador nos diera por muertos y dejara de mandar llamadas, y el
 * síntoma sería "se ha parado solo".
 */
export const HEARTBEAT_MS = 30000;

/**
 * Avisa de que el audio ya circula, para poder medir el silencio de verdad.
 *
 * Es el único dato que el servidor no puede saber: él solo ve `call.bridged`,
 * que es cuando Telnyx ACEPTA la orden de puentear, no cuando el audio llega.
 *
 * Se manda un aviso SIN hora: la pone el servidor, con el mismo reloj que
 * escribió `answered_at`. Mandar `Date.now()` mezclaría el reloj del portátil
 * del gestor con el del dyno —pueden ir descuadrados segundos enteros— y
 * estaríamos midiendo el desfase en vez del silencio.
 *
 * Falla en silencio a propósito: es telemetría. Perder un sello deja una llamada
 * sin medir, que es infinitamente mejor que un toast en mitad de una
 * conversación con un deudor.
 *
 * Lo llaman DOS sitios, y significan cosas distintas:
 *   - el SDK al abrir la pata (primera llamada de la sesión) → mide la
 *     negociación de medios de verdad;
 *   - el socket `call_bridged` (llamadas con pata reutilizada) → ahí **no hay
 *     negociación que medir**, el camino ya estaba abierto, y lo que sella es el
 *     instante en que el puente se confirma con la pata viva.
 *
 * Que se llame dos veces en la primera llamada no importa: el servidor solo
 * escribe `audio_at` si estaba vacío.
 */
export async function avisarDeAudio() {
  const { current } = useCallSessionStore.getState();
  if (!current?.callAttemptId) return;
  try {
    await api.post(`/calls/attempts/${current.callAttemptId}/audio`);
  } catch {
    // Ver arriba.
  }
}

export function getClient() {
  return client;
}

/**
 * El beep de «han descolgado», por un `<audio>` NORMAL y no por Web Audio.
 *
 * 🔴 **Tercer intento, y los dos anteriores fallaron en el mismo sitio.** Con
 * `AudioContext` el código se ejecutaba y no salía sonido: Chrome crea el
 * contexto suspendido y **no lanza ningún error**. Se arregló reutilizando un
 * único contexto creado desde el gesto del usuario y llamando a `resume()`…
 * y en el navegador del gestor **seguía sin sonar**, con el arreglo desplegado y
 * verificado en el bundle de producción.
 *
 * Lo que partió el problema fue una sola pregunta: la **notificación del sistema
 * sí le salía**. O sea que el socket llega, el manejador corre, y lo único mudo
 * es Web Audio.
 *
 * Así que se usa el mecanismo que en su máquina **está demostrado que suena**:
 * un elemento `<audio>`, igual que el que le hace oír al deudor.
 *
 * El tono se genera como WAV en memoria para no depender de ningún fichero que
 * alguien pueda mover, y se «desbloquea» con un `play()/pause()` mudo dentro del
 * clic de «Activar sesión» — que es lo que exige la política de medios.
 */
function wavDeUnBeep({ hz = 950, ms = 170, huecoMs = 85, pulsos = 2, sr = 22050, volumen = 0.9 } = {}) {
  const porPulso = Math.floor((sr * ms) / 1000);
  const hueco = Math.floor((sr * huecoMs) / 1000);
  const n = porPulso * pulsos + hueco * (pulsos - 1);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const txt = (pos, s) => [...s].forEach((c, i) => v.setUint8(pos + i, c.charCodeAt(0)));
  txt(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); txt(8, 'WAVE');
  txt(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  txt(36, 'data'); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i += 1) {
    // Rampa de entrada y salida: un tono que empieza y acaba en seco suena a
    // «clic» y se confunde con un fallo de audio, que es justo lo que no
    // queremos transmitirle al gestor en mitad de una llamada.
    // Dónde cae esta muestra dentro del ciclo «pulso + silencio».
    const dentro = i % (porPulso + hueco);
    const env =
      dentro < porPulso
        ? Math.min(1, dentro / (sr * 0.006), (porPulso - dentro) / (sr * 0.012))
        : 0;
    v.setInt16(44 + i * 2, Math.round(32767 * volumen * env * Math.sin((2 * Math.PI * hz * i) / sr)), true);
  }
  let bin = '';
  new Uint8Array(buf).forEach((b) => {
    bin += String.fromCharCode(b);
  });
  return `data:audio/wav;base64,${btoa(bin)}`;
}

/** El elemento del beep. Uno solo, vivo toda la sesión. */
let cueEl = null;

/**
 * Prepara el beep. **Tiene que llamarse desde el gesto del usuario.**
 *
 * El `play()` seguido de `pause()` con el volumen a cero es lo que «desbloquea»
 * el elemento: a partir de ahí Chrome le deja sonar cuando queramos, aunque
 * hayan pasado horas desde el clic.
 */
export function prepararCue() {
  try {
    if (!cueEl) {
      cueEl = new Audio(wavDeUnBeep());
      cueEl.preload = 'auto';
      // 🔴 Al máximo, y no es cosmético. Durante el ~1,27 s que Telnyx tarda en
      // hacer el puente NO EXISTE camino de audio hacia el gestor (medido el
      // 30/09/2026: 73% de las veces el deudor dice su primera palabra antes de
      // que la llamada exista). Este pitido suena en el navegador, no viaja por
      // la llamada: es lo ÚNICO que puede avisar dentro de ese hueco. Bajarlo
      // devuelve el síntoma de «no oigo el principio de la llamada».
      cueEl.volume = 1;
    }
    const antes = cueEl.volume;
    cueEl.volume = 0;
    const p = cueEl.play();
    if (p && typeof p.then === 'function') {
      p.then(() => {
        cueEl.pause();
        cueEl.currentTime = 0;
        cueEl.volume = antes;
      }).catch(() => {
        cueEl.volume = antes;
      });
    }
    return cueEl;
  } catch {
    return null;
  }
}

export function playCue() {
  try {
    const el = cueEl || prepararCue();
    if (!el) return;
    el.currentTime = 0;
    const p = el.play();
    // Falla en silencio: un beep no puede tumbar una llamada. Pero el aviso NO
    // se queda solo en esto — la notificación del sistema va por su cuenta,
    // porque esta vía ya ha fallado antes de formas que no dan error.
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    // Ver arriba.
  }
}

/**
 * Abre sesión: credencial efímera, cliente WebRTC y a la escucha.
 *
 * ⚠️ La `TELNYX_API_KEY` NUNCA baja al navegador. El backend emite un token de
 * vida corta; con la API key se podría comprar numeración, llamar a cualquier
 * destino y leer grabaciones.
 */
export async function startSession() {
  if (busy) return;
  busy = true;

  const store = useCallSessionStore.getState();
  store.setStatus(SESSION_STATUS.CONNECTING);

  // Antes de nada. Sin micrófono la sesión NO se abre: es preferible que el
  // gestor vea un error al activar que descubrir por un deudor enfadado que
  // llevaba media hora hablándole a nadie.
  if (!(await asegurarMicrofono())) {
    toast.error('Sin micrófono no se puede abrir la sesión. Permítelo en el candado de la barra de direcciones y vuelve a activar.');
    store.setStatus(SESSION_STATUS.OFFLINE);
    busy = false;
    return;
  }

  // 🔴 AQUÍ, y no en el primer beep: esto cuelga directamente del clic en
  // «Activar sesión», y un `AudioContext` creado lejos de un gesto del usuario
  // nace suspendido y no suena — sin dar error.
  prepararCue();

  // No bloquea: si lo deniega, la sesión se abre igual. Se pide aquí y no al
  // entrar la llamada porque un diálogo de permiso mientras suena el teléfono es
  // exactamente lo que ya nos costó el audio de una conversación entera.
  await pedirPermisoDeAvisos();

  try {
    const { data } = await api.post('/calls/sessions');

    const next = new TelnyxRTC({ login_token: data.token });

    next.on('telnyx.ready', () => {
      const s = useCallSessionStore.getState();
      s.setStatus(SESSION_STATUS.READY);
      s.setQueueDepth(data.queueDepth ?? null);
    });

    // ⚠️ Los avisos de calidad NO viajan por `telnyx.notification`: tienen su
    // propio evento. Por eso llevaban desde siempre solo en la consola.
    next.on('telnyx.warning', (evento) => {
      const aviso = evento?.warning;
      if (!aviso || !AVISOS_DE_MICROFONO.has(aviso.name)) return;
      useCallSessionStore.getState().setMediaWarning({
        code: aviso.code,
        name: aviso.name,
        message: aviso.message,
      });
    });

    next.on('telnyx.error', (error) => {
      toast.error(getErrorMessage(error) || 'Se perdió la conexión de audio');
      useCallSessionStore.getState().setStatus(SESSION_STATUS.OFFLINE);
    });

    next.on('telnyx.notification', (notification) => {
      const call = notification?.call;
      if (!call) return;
      const s = useCallSessionStore.getState();

      // `ringing` es la llamada del marcador entrando al gestor. Se descuelga
      // sola: el gestor ya dijo que estaba listo al activar sesión, y hacerle
      // pulsar "aceptar" en cada llamada devolvería la mecánica que el marcador
      // vino a quitar.
      // Se descuelga sola: el gestor ya dijo que estaba listo al activar sesión.
      //
      // El beep NO va aquí. Con la pata permanente esto ocurre una vez por sesión
      // —y encima antes de que haya nadie al teléfono, porque lo que está sonando
      // es la pata del gestor, no la del deudor—. El aviso va en `call_bridged`.
      if (call.state === 'ringing') {
        // 🔴 **Sin el procesado de audio de Chrome.**
        //
        // Hoy el SDK pide el micrófono con `audio: true` —su valor por defecto,
        // ver `_retrieveLocalStream`—, y eso enciende las TRES cosas:
        // cancelación de eco, supresión de ruido y control automático de
        // ganancia. `CallOptions.audio` acepta restricciones, así que se las
        // ponemos justo antes de descolgar, que es cuando el SDK captura.
        //
        // El motivo está medido. La caja negra del 15/10/2026 pilló la ganancia
        // desplomándose en escalones tras un pico que satura, con TODO lo demás
        // intacto —`currentDirection` en `sendrecv`, la pista `live` y sin
        // mutear por el sistema, el mismo id de pista—:
        //
        //     hablas → nivel 0,99996   (saturado)
        //     hablas → nivel 0,42014
        //     hablas → nivel 0,00213   (470 veces menos)
        //
        // Nueve deudores de verdad descolgaron ese día, hablaron entre 2 y 12
        // segundos, y no les contestó nadie.
        //
        // ⚠️ **Se apagan las tres juntas a propósito, y es provisional.** El
        // sospechoso por lo medido es la ganancia, y el segundo la supresión de
        // ruido —si decide que la voz del gestor es ruido, la corta entera—. El
        // eco ya se descartó como única causa: el 15/10 siguió fallando con
        // auriculares. Apagarlas a la vez dice en una jornada si la familia
        // entera era el problema; luego se vuelven a encender de una en una.
        //
        // Y hay una contrapartida que hay que recordar cuando se reactiven: **sin
        // cancelación de eco, un gestor que llame por altavoz le devuelve su
        // propia voz al deudor.** Mientras esto esté así, auriculares.
        // ⚠️ **Se reemplaza el objeto entero, no se asigna la propiedad.**
        //
        // `call.options.audio = …` sobre una llamada sin `options` lanza, y si
        // esta línea lanza **`answer()` no se ejecuta y la llamada no se
        // descuelga nunca** — un fallo mucho peor que el que esto arregla. Pasó
        // al escribirlo, y lo pilló un test que pasaba una llamada sin
        // `options`. La forma de abajo no puede lanzar: `{ ...undefined }` es
        // legal y da `{}`.
        call.options = { ...call.options, audio: SIN_PROCESADO };
        call.answer();
        return;
      }
      // 🔴 **El instante en que el deudor deja de oír silencio.** Es el único
      // dato que el servidor no puede saber: él solo ve `call.bridged`, que es
      // cuando Telnyx ACEPTA la orden de puentear, no cuando el audio circula.
      //
      // Medido el 24/09/2026: el panel decía 3,1 s y cuatro grabaciones reales
      // coinciden en que el gestor no entra hasta el segundo 6.
      //
      // Se manda un aviso sin hora: la pone el servidor, con el mismo reloj que
      // escribió `answered_at`. Mandar `Date.now()` mezclaría el reloj del
      // portátil con el del dyno y mediríamos el desfase en vez del silencio.
      // La pata de audio del gestor ya está abierta. **Ocurre una vez por
      // sesión**, no una por llamada: desde que la pata se aparca entre llamadas,
      // el SDK solo ve la primera. Quien dice "esta llamada está puenteada" es el
      // socket `call_bridged`, no esto.
      if (call.state === 'active') {
        avisarDeAudio();
        s.answered(call);
      }
      // Se murió la pata de audio de la SESIÓN —navegador sin red, Telnyx que la
      // mata a las 4 h—. Si pasó en mitad de una llamada, el servidor ya le está
      // colgando al deudor y mandará `call_ended`; esto es la red de seguridad
      // local, y `ended()` sale solo si no había llamada en curso.
      if (call.state === 'hangup' || call.state === 'destroy') {
        s.answered(null);
        s.ended();
      }
    });

    next.connect();
    // El elemento vive en `CallSessionHost`, por encima del router: si lo
    // pintara una pantalla, navegar dejaría el audio sin dónde sonar.
    next.remoteElement = 'call-audio';

    client = next;
    store.attachClient(next);
  } catch (error) {
    toast.error(getErrorMessage(error));
    useCallSessionStore.getState().setStatus(SESSION_STATUS.OFFLINE);
  } finally {
    busy = false;
  }
}

/**
 * Colgar la llamada en curso.
 *
 * 🔴 **Esto no existía.** El plan dice que el gestor «nunca cuelga un
 * no-contesta» —que los descarta el sistema— y se implementó como «el gestor
 * nunca cuelga», que es otra cosa. Resultado: la única forma de terminar una
 * llamada era que colgara el deudor.
 *
 * Un deudor que se enrolla, que insulta, o que no es la persona y no cuelga,
 * dejaba al gestor atrapado — y **con la llamada abierta no le entra la
 * siguiente**, así que su jornada entera dependía de que colgara alguien que no
 * tenía ningún motivo para hacerlo.
 *
 * 🔴 **Se cuelga la pata del DEUDOR, no la nuestra.** Antes se colgaba la nuestra
 * y el backend mataba la del deudor por rebote, lo cual era correcto mientras la
 * pata del gestor nacía y moría con cada llamada. Ya no: ahora vive toda la
 * jornada, y colgarla aquí le devolvería los ~5,5 s de silencio a todas las
 * llamadas siguientes — un botón de "colgar" que empeora la sesión entera.
 */
export async function hangupCall() {
  try {
    await api.post('/calls/sessions/hangup');
  } catch (error) {
    toast.error(getErrorMessage(error) || 'No se pudo colgar');
  }
}

/**
 * Permiso para avisar por notificación del sistema.
 *
 * Se pide al activar sesión, junto al micrófono, y **no bloquea**: sin
 * notificaciones la sesión funciona igual, solo que el gestor tiene que estar
 * mirando la pestaña para enterarse de que entra una llamada.
 */
export async function pedirPermisoDeAvisos() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

/**
 * Avisa de una llamada entrante FUERA del navegador.
 *
 * La barra flotante solo existe mientras el gestor mira la pestaña de ClaimUp.
 * Si está en otra aplicación, la llamada **se descuelga sola** y se encontraría
 * hablando con alguien sin saber quién es. Esto es lo único que sale del
 * navegador y se lo puede decir.
 */
/**
 * «Te están llamando» — un deudor que devuelve la llamada.
 *
 * 🔴 Hasta el 30/09/2026 una entrante se descolgaba en el servidor y **el gestor
 * no se enteraba de nada**: ni ficha, ni aviso, ni sonido. Edgar lo dijo así:
 * «no he oído nada, no sabía que me estaban llamando». La persona colgó.
 *
 * Y es la llamada que menos se puede perder: quien devuelve una llamada quiere
 * hablar. En una saliente hay ~22 s de tono para reaccionar; aquí **no hay
 * margen ninguno**, ya está al otro lado.
 *
 * Por eso este aviso es distinto del de una saliente:
 *
 *  - **suena el cue INMEDIATAMENTE**, sin esperar al puente — el puente de una
 *    entrante llega en menos de un segundo y esperarlo es perder el único aviso
 *    que hay;
 *  - **`requireInteraction`**, porque si se desvanece no queda ni rastro;
 *  - y **tag propio**: con el de las salientes, la siguiente marcación borraría
 *    el aviso de la persona que está esperando ahora mismo.
 *
 * 🔴 **Y NO dice «descuelga».** Lo decía, y mandaba a buscar un botón que no
 * existe: la entrante **ya está descolgada y puenteada** cuando esto aparece. Lo
 * hace el servidor —`answer_inbound` en `calls.service.js`— contra la pata de
 * audio que el gestor tiene abierta, y este aviso sale justo detrás. Medido el
 * 15/10/2026: entrante a las 09:03:41, descolgada y puenteada a las 09:03:42.
 *
 * Edgar, ese día: «me ha saltado aviso de que me estaban llamando pero no he
 * sabido cómo contestar». Estuvo once segundos conectado con esa persona
 * buscando el botón. Un aviso que pide una acción imposible es peor que no
 * tenerlo: gasta el único margen que hay, que son esos segundos.
 */
export function avisarDeEntrante(target) {
  playCue();

  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const n = new Notification('📞 Ya estás hablando con alguien — habla', {
      body: target?.debtorName
        ? `${target.debtorName} te devuelve la llamada. Está conectado: no hay nada que pulsar.`
        : 'Un deudor te devuelve la llamada. Está conectado: no hay nada que pulsar.',
      tag: 'claimup-entrante',
      renotify: true,
      requireInteraction: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Sin service worker algunos navegadores no notifican. El cue ya ha sonado.
  }
}

export function avisarDeLlamada(target) {
  if (!target || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

  const euros =
    typeof target.debt === 'number'
      ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(target.debt)
      : '';

  try {
    const n = new Notification(target.debtorName || 'Llamada entrante', {
      body: [euros, target.clientName].filter(Boolean).join(' · '),
      // Una llamada por expediente: si el gestor no la ha visto, la siguiente la
      // reemplaza en vez de apilar veinte avisos muertos.
      tag: 'claimup-llamada',
      renotify: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Algunos navegadores exigen service worker para notificar. No es motivo
    // para tumbar nada: el aviso es un extra, la llamada sigue su curso.
  }
}

/**
 * «Han descolgado. Habla.» — por notificación del sistema.
 *
 * 🔴 **Es la señal que NO depende de la política de audio de Chrome.**
 *
 * El beep se apoya en `AudioContext`, y un contexto nace **suspendido** si no
 * cuelga de un gesto del usuario — comprobado el 25/09/2026 contra el navegador
 * real: `{estado: "suspended"}`. No da error: simplemente no suena. Ya nos costó
 * una jornada de llamadas en las que el gestor no sabía cuándo le descolgaban.
 *
 * Así que hay dos avisos por caminos independientes. Si uno se cae, queda el otro,
 * y eso es deliberado: enterarse de que hay alguien al teléfono es la diferencia
 * entre una conversación y cuatro segundos de silencio.
 *
 * El permiso ya se pidió al activar sesión, así que aquí no hay diálogos.
 */
export function avisarDeDescuelgue(target) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const n = new Notification(`Han descolgado — habla`, {
      body: target?.debtorName || 'Di tu nombre y de parte de quién llamas',
      // Reemplaza al aviso de «entra una llamada» en vez de apilarse debajo: son
      // el mismo suceso en dos momentos, no dos cosas que atender.
      tag: 'claimup-llamada',
      renotify: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Sin service worker algunos navegadores no notifican. No es motivo para nada.
  }
}

/**
 * «Tienes una llamada sin clasificar.»
 *
 * 🔴 El marcador se para a propósito mientras hay algo que tipificar, y eso está
 * bien: no se le mete otra llamada encima al gestor. Lo que no estaba era
 * **decírselo**. Desde fuera, un marcador que espera y uno averiado se ven
 * exactamente igual — no entra nada, y ya está.
 *
 * Visto en producción el 29/09/2026: ocho marcaciones sin tipificar y la sesión
 * parada minutos mientras Edgar trabajaba en otra pantalla. «El problema es que
 * estoy haciendo otras cosas y no me doy cuenta.»
 *
 * Por eso va por notificación del SISTEMA y no por un toast: es el único camino
 * que está comprobado que le llega **con la pestaña de fondo**, que es justo
 * cuando pasa. Lo confirmó él mismo el 28/09 con el aviso de descuelgue.
 *
 * `requireInteraction` es deliberado: este aviso no se autodescarta a los pocos
 * segundos como el de descuelgue. Mientras no tipifique no entran llamadas, así
 * que el aviso tiene que seguir ahí cuando vuelva a mirar la pantalla.
 */
export function avisarDeTipificar(pendiente) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const n = new Notification('Clasifica la llamada — no entran más hasta entonces', {
      body: pendiente?.debtorName || 'Tienes una llamada terminada sin tipificar',
      // Tag PROPIO, distinto del de la llamada: son dos cosas que atender, no el
      // mismo suceso en dos momentos. Con el mismo tag, el aviso de la siguiente
      // llamada borraría este — y se volvería a perder.
      tag: 'claimup-tipificar',
      renotify: true,
      requireInteraction: true,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Sin service worker algunos navegadores no notifican. No es motivo para nada.
  }
}

/** Cierra sesión de verdad. Es lo único, junto al cierre de pestaña, que la para. */
export async function stopSession() {
  try {
    await api.delete('/calls/sessions');
  } catch (error) {
    // Cerrar en el backend puede fallar; la conexión local se suelta igual,
    // porque dejar el audio abierto sería peor.
    toast.warn(getErrorMessage(error));
  }
  client?.disconnect();
  client = null;
  useCallSessionStore.getState().reset();
}

/**
 * Pausa y reanuda. **Es la única forma de que el gestor deje de recibir llamadas
 * sin cerrar sesión**, y por eso navegar ya no cuenta como pausa.
 */
export async function changeAvailability(next) {
  const store = useCallSessionStore.getState();
  try {
    await api.patch('/calls/sessions', { status: next });
    if (next === SESSION_STATUS.PAUSED) store.pause();
    else store.resume();
  } catch (error) {
    toast.error(getErrorMessage(error));
  }
}

/**
 * Tipifica la marcación anterior.
 *
 * Optimista a propósito: el gestor no debe esperar a la red para seguir, y la
 * siguiente llamada ya está sonando mientras pulsa.
 */
export async function submitOutcome(outcome) {
  const store = useCallSessionStore.getState();
  const pending = store.pendingOutcome;
  if (!pending) return null;

  store.resolveOutcome(outcome);
  try {
    const { data } = await api.post(`/calls/attempts/${pending.callAttemptId}/outcome`, { outcome });
    // Lo que la pantalla necesita después: a qué expediente ir, y qué llamada
    // se acaba de programar por si el gestor quiere cancelarla. Se devuelve en
    // vez de guardarse en el store porque son acciones de UN momento concreto,
    // no estado de la sesión.
    const acciones = {
      outcome,
      debtorName: pending.debtorName || null,
      expedientId: data?.expedientId || pending.expedientId || null,
      nextAssignmentId: data?.nextAssignmentId || null,
    };
    store.ofrecerAcciones(acciones);
    return acciones;
  } catch (error) {
    toast.error(getErrorMessage(error));
    return null;
  }
}

/**
 * Crea la tarea que el gestor acaba de acordar con el deudor.
 *
 * Es lo que sustituye al reintento automático que `payment_promise` nunca hizo:
 * el plazo lo pone quien habló, no el código.
 */
export async function crearTareaDeSeguimiento({ expedientId, date, taskType = 'follow_up_call', description }) {
  try {
    await api.post('/assignments', {
      expedient_id: String(expedientId),
      task_type: taskType,
      date,
      description: description || 'Seguimiento acordado en la llamada',
      priority: 'medium',
    });
    toast.success('Tarea creada');
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error));
    return false;
  }
}

/** Cancela la llamada que la tipificación acaba de programar. */
export async function cancelarTareaProgramada(assignmentId) {
  try {
    await api.put(`/assignments/${assignmentId}`, { status: 'cancelled' });
    toast.success('Llamada cancelada');
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error));
    return false;
  }
}

/**
 * Latido. Sin esto el backend da la sesión por muerta a los dos minutos.
 *
 * La ruta existía desde el primer día y **no la llamaba nadie**: el gestor se
 * quedaba sin llamadas a los dos minutos de activar sesión, estuviera donde
 * estuviera, y parecía que el marcador se paraba solo.
 */
export async function sendHeartbeat() {
  try {
    await api.post('/calls/sessions/heartbeat');
  } catch {
    // Un latido perdido no es nada: hay margen de cuatro antes de que el backend
    // dé la sesión por muerta. Avisar aquí solo llenaría la pantalla de toasts.
  }
}
